import Breadcrumb from "@/components/Breadcrumb"
import { useTranslation } from "react-i18next"
import { useFieldArray, useForm } from "react-hook-form"
import { useUpdatePermissionMutation, useGetPermissionsQuery } from "@/services/permission.service"
import { Permission } from "@/types/permission.type"
import { useEffect, useState } from "react"
import { PermissionsUserRoles } from "@/constants/default"
import { PERMISSION_MODULES, PermissionAction } from "@/constants/permissions-config"
import PermissionConfirmationModal from "./_confirmation-modal"
import ModulePermissionSection from "./_module-section"

// Permissions grouped by navigation menu - Room and Company are now part of Properties

interface PermissionCheckbox {
    id: number | null  // null for new permissions not yet in database
    module: string
    role: string
    action: PermissionAction
    value: boolean
    group: 'app' | 'api'
}

interface ModulePermissions {
    module: string
    displayName: string
    description: string
    permissions: PermissionCheckbox[]
}

interface RolePermissions {
    roleId: number
    roleName: string
    modules: ModulePermissions[]
}

export interface FormValues {
    roles: RolePermissions[]
}

export default function PermissionComponent() {
    const { t } = useTranslation()
    const listUserRoles = PermissionsUserRoles()
    const [showConfirmation, setShowConfirmation] = useState(false)

    const { handleSubmit, setValue, control, watch } = useForm<FormValues>({
        defaultValues: { roles: [] }
    })

    const { data: dbPermissions } = useGetPermissionsQuery(undefined, { refetchOnMountOrArgChange: true })
    const [updatePermissions] = useUpdatePermissionMutation()

    const { fields } = useFieldArray({
        control,
        name: 'roles',
    })

    // Build complete permission structure from config + database
    useEffect(() => {
        if (dbPermissions) {
            console.log('PERMISSION_MODULES count:', PERMISSION_MODULES.length)
            console.log('PERMISSION_MODULES:', PERMISSION_MODULES.map(m => m.displayName))

            const rolePermissions: RolePermissions[] = []

            listUserRoles.forEach(role => {
                const modules: ModulePermissions[] = []

                PERMISSION_MODULES.forEach(moduleConfig => {
                    const permissions: PermissionCheckbox[] = []

                    // Get database permissions for app module
                    const dbAppModule = dbPermissions.find(m => m.name === moduleConfig.module)

                    // Also check related modules (e.g., room, company for business-unit)
                    const relatedAppModules = moduleConfig.relatedModules?.map(relatedName =>
                        dbPermissions.find(m => m.name === relatedName)
                    ).filter(Boolean) || []

                    // Get database permissions for API module (may have different name)
                    const apiModuleName = moduleConfig.apiModule || moduleConfig.module
                    const dbApiModule = dbPermissions.find(m => m.name === apiModuleName)

                    // Also check related API modules
                    const relatedApiModules = moduleConfig.relatedApiModules?.map(relatedName =>
                        dbPermissions.find(m => m.name === relatedName)
                    ).filter(Boolean) || []

                    // Add all app permissions
                    moduleConfig.appPermissions.forEach(action => {
                        // Check main module first
                        let dbPerm = dbAppModule?.permissions.find(
                            p => p.role === role.value.toString() && p.action.replace('no-', '') === action
                        )

                        // If not found, check related modules (they should all have same permission)
                        if (!dbPerm) {
                            for (const relatedModule of relatedAppModules) {
                                dbPerm = relatedModule?.permissions.find(
                                    p => p.role === role.value.toString() && p.action.replace('no-', '') === action
                                )
                                if (dbPerm) break
                            }
                        }

                        permissions.push({
                            id: dbPerm?.id || null,
                            module: moduleConfig.module,
                            role: role.value.toString(),
                            action,
                            value: dbPerm ? !dbPerm.action.startsWith('no-') : false,
                            group: 'app'
                        })
                    })

                    // Add all API permissions (using API module name if different)
                    moduleConfig.apiPermissions.forEach(action => {
                        // Check main API module first
                        let dbPerm = dbApiModule?.permissions.find(
                            p => p.role === role.value.toString() && p.action.replace('no-', '') === action
                        )

                        // If not found, check related API modules
                        if (!dbPerm) {
                            for (const relatedModule of relatedApiModules) {
                                dbPerm = relatedModule?.permissions.find(
                                    p => p.role === role.value.toString() && p.action.replace('no-', '') === action
                                )
                                if (dbPerm) break
                            }
                        }

                        permissions.push({
                            id: dbPerm?.id || null,
                            module: apiModuleName,  // Use API module name for storage
                            role: role.value.toString(),
                            action,
                            value: dbPerm ? !dbPerm.action.startsWith('no-') : false,
                            group: 'api'
                        })
                    })

                    modules.push({
                        module: moduleConfig.module,
                        displayName: moduleConfig.displayName,
                        description: moduleConfig.description,
                        permissions
                    })
                })

                rolePermissions.push({
                    roleId: role.value,
                    roleName: role.label,
                    modules
                })
            })

            setValue('roles', rolePermissions)
        }
    }, [dbPermissions])

    const onSubmitClick = () => {
        setShowConfirmation(true)
    }

    const onConfirmSave = handleSubmit((data) => {
        const dataPost: Permission[] = []

        data.roles.forEach(role => {
            role.modules.forEach(module => {
                // Find the module config to check for related modules
                const moduleConfig = PERMISSION_MODULES.find(m => m.module === module.module)

                module.permissions.forEach(perm => {
                    const addPermission = (moduleName: string, action: string, id: number = 0) => {
                        if (id !== 0 || perm.value === true) {
                            dataPost.push({
                                id: id || 0,
                                model: moduleName,
                                role: perm.role,
                                action: perm.value ? action : `no-${action}`
                            })
                        }
                    }

                    if (perm.group === 'api') {
                        // API permissions - use API module name
                        const apiModuleName = moduleConfig?.apiModule || module.module
                        addPermission(apiModuleName, perm.action, perm.id ?? 0)

                        // Also add for related API modules
                        moduleConfig?.relatedApiModules?.forEach(relatedApiModule => {
                            addPermission(relatedApiModule, perm.action, 0)
                        })
                    } else {
                        // Regular app permission
                        addPermission(module.module, perm.action, perm.id ?? 0)

                        // Add for related modules
                        moduleConfig?.relatedModules?.forEach(relatedModule => {
                            addPermission(relatedModule, perm.action, 0)
                        })
                    }
                })
            })
        })

        updatePermissions({ permissions: dataPost })
        setShowConfirmation(false)
    })

    return (
        <>
            <Breadcrumb pageName={t("permission.permissions")} />

            <div className="flex flex-col gap-10">
                {/* Info Banner */}
                <div className="rounded-sm border border-primary/20 bg-primary/5 p-4">
                    <div className="flex items-start gap-3">
                        <i className="fa-light fa-circle-info text-primary text-xl mt-0.5"></i>
                        <div>
                            <h5 className="font-semibold text-black dark:text-white mb-1">
                                Permission Management
                            </h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Configure what each user role can access in both the web application and via the Discord bot API.
                                <strong className="text-primary"> App Permissions</strong> control web access,
                                <strong className="text-primary"> API Permissions</strong> control Discord bot access.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Permissions Form */}
                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
                    <form onSubmit={(e) => { e.preventDefault(); onSubmitClick(); }}>
                        {fields.map((roleField, roleIndex) => (
                            <div key={`role-${roleIndex}`} className="mb-5">
                                <ModulePermissionSection
                                    role={roleField}
                                    roleIndex={roleIndex}
                                    control={control}
                                    isOpen={false}
                                />
                            </div>
                        ))}

                        <div className="flex justify-end mt-6 pt-4 border-t border-stroke dark:border-strokedark">
                            <button
                                type="submit"
                                className="flex justify-center items-center rounded bg-primary py-2.5 px-6 font-medium text-white hover:bg-opacity-90"
                            >
                                <i className="fa-light fa-floppy-disk me-2 text-white" />
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Confirmation Modal */}
            <PermissionConfirmationModal
                isOpen={showConfirmation}
                onConfirm={onConfirmSave}
                onCancel={() => setShowConfirmation(false)}
                changesCount={listUserRoles.length}
            />
        </>
    )
}
